import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { ArrowLeftIcon, PencilIcon, Trash2Icon, XIcon, CheckIcon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface CdcEnRutaConfig {
  requireLoadRemito: boolean
  showTripPlanning: boolean
}

const DEFAULT_CDC_CONFIG: CdcEnRutaConfig = {
  requireLoadRemito: false,
  showTripPlanning: true,
}

interface ClientDetail {
  id: string
  code: string
  fileId: number | null
  name: string
  sgtCodes: string[] | null
  docId: string | null
  docTypeId: string | null
  docTypeCode: string | null
  docTypeName: string | null
  address: string | null
  cityName: string | null
  postalCode: string | null
  stateId: string | null
  stateName: string | null
  countryId: string | null
  countryName: string | null
  cdcEnRutaConfig: CdcEnRutaConfig | null
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

type FormState = {
  code: string; fileId: string; name: string
  docId: string; docTypeId: string; address: string; cityName: string
  postalCode: string; stateId: string; countryId: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(v: string) {
  return new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function ViewField({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>
        {value ?? <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [isEditing, setIsEditing] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form, setForm] = useState<FormState>({
    code: "", fileId: "", name: "", docId: "",
    docTypeId: "", address: "", cityName: "", postalCode: "", stateId: "", countryId: "",
  })
  const [sgtCodes, setSgtCodes] = useState<string[]>([])
  const [sgtInput, setSgtInput] = useState("")
  const [cdcConfig, setCdcConfig] = useState<CdcEnRutaConfig>(DEFAULT_CDC_CONFIG)
  const [formError, setFormError] = useState<string | null>(null)

  // ── Queries ─────────────────────────────────────────────────────────────────

  const { data: client, isLoading, isError } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/clients/${id}`)
      if (!res.ok) throw new Error("Cliente no encontrado")
      return res.json() as Promise<ClientDetail>
    },
    enabled: !!id,
  })

  const { data: docTypeOptions = [] } = useQuery({
    queryKey: ["doc-types-all"],
    queryFn: async () => {
      const res = await api["doc-types"].$get({ query: {} })
      return res.ok ? (res.json() as Promise<{ id: string; code: string; name: string }[]>) : []
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: countryOptions = [] } = useQuery({
    queryKey: ["countries-all"],
    queryFn: async () => {
      const res = await api["geo"]["countries"].$get({ query: {} })
      return res.ok ? (res.json() as Promise<{ id: string; name: string; isoCode: string }[]>) : []
    },
    staleTime: 10 * 60 * 1000,
  })

  const { data: stateOptions = [] } = useQuery({
    queryKey: ["states-by-country", form.countryId],
    queryFn: async () => {
      if (!form.countryId) return []
      const res = await api["geo"]["states"].$get({ query: { countryId: form.countryId } })
      return res.ok ? (res.json() as Promise<{ id: string; name: string; code: string }[]>) : []
    },
    enabled: !!form.countryId,
    staleTime: 10 * 60 * 1000,
  })

  // ── Mutations ────────────────────────────────────────────────────────────────

  const updateMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await api["clients"][":id"].$patch({
        param: { id: id! },
        json: {
          code:            data.code,
          fileId:          data.fileId ? parseInt(data.fileId) : null,
          name:            data.name,
          sgtCodes:        sgtCodes.length ? sgtCodes : null,
          docId:           data.docId      || null,
          docTypeId:       data.docTypeId  || null,
          address:         data.address    || null,
          cityName:        data.cityName   || null,
          postalCode:      data.postalCode || null,
          stateId:         data.stateId    || null,
          countryId:       data.countryId  || null,
          cdcEnRutaConfig: cdcConfig,
        },
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar cliente")
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client", id] })
      qc.invalidateQueries({ queryKey: ["clients"] })
      setIsEditing(false)
      setFormError(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/clients/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client", id] })
      qc.invalidateQueries({ queryKey: ["clients"] })
      setDeleteOpen(false)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API_URL}/api/clients/${id}/restore`, { method: "PATCH" })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["client", id] })
      qc.invalidateQueries({ queryKey: ["clients"] })
    },
    onError: (err: Error) => setFormError(err.message),
  })

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function openEdit() {
    if (!client) return
    setForm({
      code:       client.code,
      fileId:     client.fileId != null ? String(client.fileId) : "",
      name:       client.name,
      docId:      client.docId      ?? "",
      docTypeId:  client.docTypeId  ?? "",
      address:    client.address    ?? "",
      cityName:   client.cityName   ?? "",
      postalCode: client.postalCode ?? "",
      stateId:    client.stateId    ?? "",
      countryId:  client.countryId  ?? "",
    })
    setSgtCodes(client.sgtCodes ?? [])
    setSgtInput("")
    setCdcConfig(client.cdcEnRutaConfig ?? DEFAULT_CDC_CONFIG)
    setFormError(null)
    setIsEditing(true)
  }

  function cancelEdit() {
    setIsEditing(false)
    setFormError(null)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!form.code.trim() || !form.name.trim()) {
      setFormError("Código y razón social son requeridos")
      return
    }
    updateMut.mutate(form)
  }

  const selectedDocType = docTypeOptions.find((t) => t.id === form.docTypeId)
  const selectedCountry = countryOptions.find((c) => c.id === form.countryId)
  const selectedState   = stateOptions.find((s) => s.id === form.stateId)

  // ── Loading / error states ───────────────────────────────────────────────────

  if (isLoading) {
    return (
      <>
        <SiteHeader title="Cliente" />
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted-foreground">
          Cargando...
        </div>
      </>
    )
  }

  if (isError || !client) {
    return (
      <>
        <SiteHeader title="Cliente no encontrado" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <p className="text-sm text-muted-foreground">No se encontró el cliente solicitado.</p>
          <Button variant="outline" onClick={() => navigate("/clients")}>
            <ArrowLeftIcon className="size-4 mr-2" /> Volver a clientes
          </Button>
        </div>
      </>
    )
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title={client.name} />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ── Barra superior ── */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <ArrowLeftIcon className="size-4" /> Volver
              </button>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded tracking-wide">
                {client.code}
              </span>
              {client.deletedAt && (
                <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                  Eliminado
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              {can("write", "clients") && (
                client.deletedAt ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => restoreMut.mutate()}
                    disabled={restoreMut.isPending}
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  >
                    <RestoreIcon className="size-4 mr-1.5" />
                    {restoreMut.isPending ? "Restaurando..." : "Restaurar"}
                  </Button>
                ) : isEditing ? (
                  <>
                    <Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
                      Cancelar
                    </Button>
                    <Button type="submit" size="sm" disabled={updateMut.isPending}>
                      <CheckIcon className="size-4 mr-1.5" />
                      {updateMut.isPending ? "Guardando..." : "Guardar cambios"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" size="sm" onClick={openEdit}>
                      <PencilIcon className="size-4 mr-1.5" /> Editar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteOpen(true)}
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                    >
                      <Trash2Icon className="size-4 mr-1.5" /> Eliminar
                    </Button>
                  </>
                )
              )}
            </div>
          </div>

          {formError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
          )}

          {/* ── Identificación ── */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identificación
            </h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">

              {/* Código */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Código</Label>
                {isEditing ? (
                  <Input
                    value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                    maxLength={10}
                    className="font-mono h-8 text-sm"
                    required
                  />
                ) : (
                  <span className="font-mono text-sm">{client.code}</span>
                )}
              </div>

              {/* Razón Social */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Razón Social</Label>
                {isEditing ? (
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={200}
                    className="h-8 text-sm"
                    required
                  />
                ) : (
                  <span className="text-sm">{client.name}</span>
                )}
              </div>

              {/* N° Legajo */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">N° Legajo</Label>
                {isEditing ? (
                  <Input
                    type="number"
                    value={form.fileId}
                    onChange={(e) => setForm((f) => ({ ...f, fileId: e.target.value }))}
                    placeholder="1001"
                    className="font-mono h-8 text-sm"
                  />
                ) : (
                  <span className="font-mono text-sm">
                    {client.fileId != null ? String(client.fileId) : <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>

              {/* Códigos SGT */}
              <div className="col-span-2 sm:col-span-3 flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Códigos SGT (SmartGT)</Label>
                {isEditing ? (
                  <>
                    <div className="flex flex-wrap gap-1.5 items-center min-h-9 rounded-md border border-input px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
                      {sgtCodes.map((code, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-muted font-mono text-xs px-2 py-0.5 rounded-sm">
                          {code}
                          <button type="button" onClick={() => setSgtCodes(sgtCodes.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                            <XIcon className="size-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        value={sgtInput}
                        onChange={(e) => setSgtInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault()
                            const code = sgtInput.toUpperCase().trim()
                            if (code && !sgtCodes.includes(code)) setSgtCodes([...sgtCodes, code])
                            setSgtInput("")
                          }
                        }}
                        placeholder={sgtCodes.length === 0 ? "Escribir y Enter para agregar..." : ""}
                        className="flex-1 min-w-32 bg-transparent text-xs font-mono outline-none placeholder:text-muted-foreground"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Nombres exactos en SmartGT. Se normalizan automáticamente.</p>
                  </>
                ) : (
                  client.sgtCodes?.length ? (
                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                      {client.sgtCodes.map((s) => (
                        <span key={s} className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  ) : <span className="text-sm text-muted-foreground/50">—</span>
                )}
              </div>
            </dl>
          </div>

          {/* ── Documento ── */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Documento
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">

              {/* Tipo de documento */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Tipo de documento</Label>
                {isEditing ? (
                  <Select
                    value={form.docTypeId || "__none__"}
                    onValueChange={(v) => setForm((f) => ({ ...f, docTypeId: v === "__none__" ? "" : (v ?? "") }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="—">
                        {selectedDocType ? `${selectedDocType.code} — ${selectedDocType.name}` : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {docTypeOptions.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.code} — {t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-sm">
                    {client.docTypeName ? `${client.docTypeCode} — ${client.docTypeName}` : <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>

              {/* N° Documento */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">N° Documento</Label>
                {isEditing ? (
                  <Input
                    value={form.docId}
                    onChange={(e) => setForm((f) => ({ ...f, docId: e.target.value }))}
                    maxLength={30}
                    className="font-mono h-8 text-sm"
                    placeholder="20-12345678-9"
                  />
                ) : (
                  <span className="font-mono text-sm">
                    {client.docId ?? <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>
            </dl>
          </div>

          {/* ── Ubicación ── */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Ubicación
            </h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">

              {/* Dirección */}
              <div className="col-span-2 sm:col-span-3 flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Dirección</Label>
                {isEditing ? (
                  <Input
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    maxLength={300}
                    placeholder="Av. Corrientes 1234"
                    className="h-8 text-sm"
                  />
                ) : (
                  <span className="text-sm">
                    {client.address ?? <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>

              {/* Ciudad */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Ciudad</Label>
                {isEditing ? (
                  <Input
                    value={form.cityName}
                    onChange={(e) => setForm((f) => ({ ...f, cityName: e.target.value }))}
                    maxLength={150}
                    className="h-8 text-sm"
                  />
                ) : (
                  <span className="text-sm">
                    {client.cityName ?? <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>

              {/* Cód. postal */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Cód. postal</Label>
                {isEditing ? (
                  <Input
                    value={form.postalCode}
                    onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
                    maxLength={10}
                    className="font-mono h-8 text-sm"
                  />
                ) : (
                  <span className="font-mono text-sm">
                    {client.postalCode ?? <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>

              {/* Provincia */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">Provincia</Label>
                {isEditing ? (
                  <Select
                    value={form.stateId || "__none__"}
                    onValueChange={(v) => setForm((f) => ({ ...f, stateId: v === "__none__" ? "" : (v ?? "") }))}
                    disabled={!form.countryId}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="—">
                        {selectedState ? selectedState.name : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {stateOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-sm">
                    {client.stateName ?? <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>

              {/* País */}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground font-normal">País</Label>
                {isEditing ? (
                  <Select
                    value={form.countryId || "__none__"}
                    onValueChange={(v) => setForm((f) => ({ ...f, countryId: v === "__none__" ? "" : (v ?? ""), stateId: "" }))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="—">
                        {selectedCountry ? selectedCountry.name : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {countryOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-sm">
                    {client.countryName ?? <span className="text-muted-foreground/50">—</span>}
                  </span>
                )}
              </div>
            </dl>
          </div>

          {/* ── CDC En Ruta Config ── */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              CDC En Ruta
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">

              {/* requireLoadRemito */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">Remito de carga obligatorio</span>
                  <span className="text-xs text-muted-foreground">El chofer debe adjuntar el remito antes de cargar</span>
                </div>
                {isEditing ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cdcConfig.requireLoadRemito}
                    onClick={() => setCdcConfig((c) => ({ ...c, requireLoadRemito: !c.requireLoadRemito }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      cdcConfig.requireLoadRemito ? "bg-primary" : "bg-input"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block size-5 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                        cdcConfig.requireLoadRemito ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                ) : (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    (client.cdcEnRutaConfig ?? DEFAULT_CDC_CONFIG).requireLoadRemito
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {(client.cdcEnRutaConfig ?? DEFAULT_CDC_CONFIG).requireLoadRemito ? "Activo" : "Inactivo"}
                  </span>
                )}
              </div>

              {/* showTripPlanning */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium">Mostrar planificación de viaje</span>
                  <span className="text-xs text-muted-foreground">El chofer ve la planificación completa del viaje</span>
                </div>
                {isEditing ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cdcConfig.showTripPlanning}
                    onClick={() => setCdcConfig((c) => ({ ...c, showTripPlanning: !c.showTripPlanning }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      cdcConfig.showTripPlanning ? "bg-primary" : "bg-input"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block size-5 rounded-full bg-background shadow-lg ring-0 transition-transform ${
                        cdcConfig.showTripPlanning ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                ) : (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    (client.cdcEnRutaConfig ?? DEFAULT_CDC_CONFIG).showTripPlanning
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {(client.cdcEnRutaConfig ?? DEFAULT_CDC_CONFIG).showTripPlanning ? "Activo" : "Inactivo"}
                  </span>
                )}
              </div>

            </dl>
          </div>

          {/* ── Registro ── */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Registro
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <ViewField label="Creado"      value={fmtDate(client.createdAt)} />
              <ViewField label="Actualizado" value={fmtDate(client.updatedAt)} />
              {client.deletedAt && (
                <ViewField label="Eliminado" value={fmtDate(client.deletedAt)} />
              )}
            </dl>
          </div>

        </form>
      </div>

      {/* ── Delete dialog ─────────────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false} className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar cliente</DialogTitle>
            <DialogDescription>
              ¿Eliminás el cliente <strong>{client.code}</strong> — {client.name}?
              Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
